import Vue from "vue";
import { ActionContext, Module } from "vuex";

import { RootState } from "@/store/root";

export interface Settings {
  showAllDuplicates: boolean;
  endpoints: {
    analysis: string;
    encode: string;
    customEncode: string;
    snomed: string;
    msa: string;
    lien: string;
    tokenizer: string;
    search: string;
    verify: string;
    textractor: string;
  };
}

export interface SettingsState {
  current: Settings;
}

class LocalStorageManager {
  private key: string;

  constructor(key: string) {
    this.key = key;
  }

  static sanitize<T extends { [key: string]: any }>(
    data: any,
    reference: T
  ): T {
    if (!data) return reference;

    return Object.assign(
      {},
      ...Object.keys(reference).map(key => ({
        [key]:
          key in data && typeof data[key] === typeof reference[key]
            ? typeof reference[key] === "object"
              ? this.sanitize(data[key], reference[key])
              : data[key]
            : reference[key]
      }))
    );
  }

  static default(): Settings {
    return {
      showAllDuplicates: false,
      endpoints: {
        analysis:
          process.env.VUE_APP_ANALYSIS_URL ||
          "https://api.verbotics.io/miranda/mine/plaintext/st",
        encode:
          process.env.VUE_APP_ENCODE_URL ||
          "https://api.verbotics.io/miranda/icdencode",
        customEncode:
          process.env.VUE_APP_CUSTOM_ENCODE_URL ||
          "https://sandbox.verbotics.io/api/v1/encoding/custom",
        snomed:
          process.env.VUE_APP_SNOMED_URL ||
          "https://api.verbotics.io/miranda/snomedencode",
        msa:
          process.env.VUE_APP_MSA_URL || "https://api.verbotics.io/api/v1/msa/",
        lien:
          process.env.VUE_APP_LIEN_URL ||
          "https://api.verbotics.io/api/v1/lien/",
        tokenizer:
          process.env.VUE_APP_TOKENIZER_URL ||
          "https://api.verbotics.io/api/v1/tokens/",
        search:
          process.env.VUE_APP_SEARCH_URL ||
          "https://api.verbotics.io/miranda/isearch/20",
        verify:
          process.env.VUE_APP_VERIFY_URL ||
          "https://api.verbotics.io/api/v1/verify",
        textractor:
          process.env.VUE_APP_TEXTRACT_URL || "https://api.verbotics.io/pdf"
      }
    };
  }

  retrieve(): Settings {
    const reference = LocalStorageManager.default();

    try {
      return LocalStorageManager.sanitize(
        JSON.parse(localStorage.getItem(this.key) || "{}"),
        reference
      );
    } catch (exception) {
      console.error(exception);
    }

    return reference;
  }

  commit(data: Settings): void {
    localStorage.setItem(this.key, JSON.stringify(data));
  }
}

const manager = new LocalStorageManager("settings");

export const settings: Module<SettingsState, RootState> = {
  state: {
    current:
      process.env.VUE_APP_CUSTOM_ENDPOINTS_ENABLED === "1"
        ? manager.retrieve()
        : LocalStorageManager.default()
  },
  namespaced: true,
  mutations: {
    SET(
      state: SettingsState,
      params: {
        key:
          | "analysis"
          | "encode"
          | "customEncode"
          | "search"
          | "verify"
          | "textractor"
          | "showAllDuplicates";
        value: any;
      }
    ) {
      if (Object.keys(state.current.endpoints).includes(params.key)) {
        Vue.set(state.current.endpoints, params.key, params.value);
      } else {
        Vue.set(state.current, params.key, params.value);
      }
    },
    RESET(state: SettingsState) {
      state.current = LocalStorageManager.default();
    }
  },
  actions: {
    set(
      context: ActionContext<SettingsState, RootState>,
      params: {
        key:
          | "analysis"
          | "encode"
          | "search"
          | "customEncode"
          | "verify"
          | "showAllDuplicates";
        value: string;
      }
    ) {
      context.commit("SET", params);
      manager.commit(context.state.current);
    },
    reset(context: ActionContext<SettingsState, RootState>) {
      context.commit("RESET");
      manager.commit(context.state.current);
    }
  }
};
